import {
  computed,
  inject,
  Injectable,
  Injector,
  runInInjectionContext,
  signal,
  Signal,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { concat, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { LoggerService } from './logger.service';
import { NetworkStateService } from './network-state.service';

export interface ReactiveHttpQueryResult<T> {
  data: Signal<T | null>;
  loading: Signal<boolean>;
  error: Signal<string | null>;
  refresh: () => void;
}

export interface ReactiveHttpOptions {
  /** If true, only makes request when NOT in offline mode (default: true) */
  respectOfflineMode?: boolean;
  /** Custom error message to show on failure */
  errorMessage?: string;
  /** Logger context name (for debugging) */
  loggerContext?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ReactiveHttpService {
  private networkState = inject(NetworkStateService);
  private loggerService = inject(LoggerService);
  private injector = inject(Injector);

  /**
   * Create a reactive HTTP query that automatically:
   * - Respects offline mode (optional)
   * - Tracks loading/error states
   * - Supports manual refresh
   * - Cancels previous requests when parameters change
   *
   * Similar to DatabaseService.createQuery() but for HTTP calls.
   *
   * @param queryFn Function that returns an Observable (typically an HTTP call)
   * @param options Configuration options
   * @returns Signals for data, loading, error, and a refresh function
   *
   * @example
   * // In component:
   * const result = this.reactiveHttp.createQuery(
   *   () => this.apiService.getEnvironmentInfo(),
   *   { errorMessage: 'Failed to load backend info' }
   * );
   *
   * this.backendInfo = result.data;
   * this.loading = result.loading;
   * this.error = result.error;
   * // Call result.refresh() to manually refetch
   */
  createQuery<T>(
    queryFn: () => Observable<T>,
    options: ReactiveHttpOptions = {},
  ): ReactiveHttpQueryResult<T> {
    const {
      respectOfflineMode = true,
      errorMessage = 'Failed to load data',
      loggerContext = 'reactive-http',
    } = options;

    const logger = this.loggerService.getLogger(loggerContext);
    const triggerSignal = signal<number>(0);

    // Create computed signal that tracks trigger + offline mode
    const queryTrigger$ = computed(() => {
      const trigger = triggerSignal(); // Track for manual refresh
      const isOffline = respectOfflineMode ? this.networkState.stayOfflineMode() : false;

      // Return null if offline (don't fetch)
      if (isOffline) {
        return null;
      }

      // Return trigger value - any change will cause refetch
      return trigger;
    });

    // Convert computed signal to observable and create reactive query
    // CRITICAL: Use shareReplay(1) to prevent multiple subscriptions from triggering duplicate HTTP calls
    // Without this, each toSignal() below creates a new subscription → duplicate requests
    // Must be called within injection context
    const query$ = runInInjectionContext(this.injector, () =>
      toObservable(queryTrigger$).pipe(
        switchMap((trigger) => {
          if (trigger === null) {
            // Offline mode - return empty state immediately
            return of({
              data: null as T | null,
              loading: false,
              error: null as string | null,
            });
          }

          // Online mode (or offline mode not respected) - fetch from API
          return concat(
            // Emit loading state first
            of({ data: null, loading: true, error: null }),
            // Then fetch data
            queryFn().pipe(
              map((response) => ({ data: response, loading: false, error: null })),
              catchError((err) => {
                logger.error('HTTP query failed:', err);
                return of({
                  data: null,
                  loading: false,
                  error: errorMessage,
                });
              }),
            ),
          );
        }),
        shareReplay({ bufferSize: 1, refCount: true }), // Share subscription across multiple toSignal() calls
      ),
    );

    // Convert observable state to individual signals
    // Must be called within injection context
    const data = runInInjectionContext(this.injector, () =>
      toSignal(query$.pipe(map((state) => state.data)), { initialValue: null }),
    );

    const loading = runInInjectionContext(this.injector, () =>
      toSignal(query$.pipe(map((state) => state.loading)), { initialValue: false }),
    );

    const error = runInInjectionContext(this.injector, () =>
      toSignal(query$.pipe(map((state) => state.error)), { initialValue: null }),
    );

    // Manual refresh function
    const refresh = () => {
      triggerSignal.update((n) => n + 1);
    };

    return { data, loading, error, refresh };
  }
}
